// src/components/admin/onboarding/AdminOnboardingReview.js
// UI redesign of the admin onboarding review queue. All data comes from the
// existing OnboardingContext, so the functional layer (fetchOnboardingData,
// reviewAndApprove, getPendingOnboarding, getCompletedOnboarding,
// getCompletionPercentage, getItemStatus, ONBOARDING_CHECKLIST) is unchanged.
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import {
    User, Briefcase, FileText, Landmark, CheckCircle2, AlertTriangle,
    Clock, Plus, Filter, Check, Eye, X, RefreshCw,
} from 'lucide-react';
import { useOnboarding } from '../../../contexts/OnboardingContext';
import { useEmployees } from '../../../contexts/EmployeeContext';
import { useAuth } from '../../../hooks/useAuth';
import { useTheme } from '../../../contexts/ThemeContext';
import { ONBOARDING_CHECKLIST } from '../../../contexts/OnboardingChecklistConfig';
import { palette as buildPalette } from './onboardingTheme';

/* Palette context — every primitive in this file reads from here */
const PaletteCtx = createContext(buildPalette(false));
const useP = () => useContext(PaletteCtx);

/* ── small UI primitives (scoped, no global CSS) ── */
const Card = ({ children, padding = 24, style = {}, ...rest }) => {
    const p = useP();
    return (
        <div {...rest} style={{
            background: p.surface, borderRadius: 12, padding,
            border: `1px solid ${p.border}`, boxShadow: p.cardShadow,
            ...style,
        }}>{children}</div>
    );
};

const Pill = ({ children, tone = 'gray' }) => {
    const p = useP();
    const tones = {
        gray:  { bg: p.pillGrayBg,  fg: p.pillGrayFg,  bd: p.pillGrayBd },
        blue:  { bg: p.pillBlueBg,  fg: p.pillBlueFg,  bd: p.pillBlueBd },
        green: { bg: p.pillGreenBg, fg: p.pillGreenFg, bd: p.pillGreenBd },
        amber: { bg: p.pillAmberBg, fg: p.pillAmberFg, bd: p.pillAmberBd },
        red:   { bg: p.pillRedBg,   fg: p.pillRedFg,   bd: p.pillRedBd },
    };
    const t = tones[tone] || tones.gray;
    return (
        <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            background: t.bg, color: t.fg, border: `1px solid ${t.bd}`,
            padding: '3px 8px', borderRadius: 999, fontSize: 11.5, fontWeight: 600,
            whiteSpace: 'nowrap',
        }}>{children}</span>
    );
};

const Btn = ({ children, variant = 'primary', leftIcon, rightIcon, style = {}, disabled, ...rest }) => {
    const p = useP();
    const variants = {
        primary:   { bg: p.btnPrimaryBg, fg: p.btnPrimaryFg, bd: p.btnPrimaryBd },
        secondary: { bg: p.btnSecondBg,  fg: p.btnSecondFg,  bd: p.btnSecondBd },
        ghost:     { bg: 'transparent',  fg: p.btnGhostFg,   bd: 'transparent' },
        success:   { bg: p.btnSuccessBg, fg: p.btnSuccessFg, bd: p.btnSuccessBg },
        danger:    { bg: p.btnDangerBg,  fg: p.btnDangerFg,  bd: p.btnDangerBd },
        dark:      { bg: p.btnDarkBg,    fg: p.btnDarkFg,    bd: p.btnDarkBd },
    };
    const v = variants[variant] || variants.primary;
    return (
        <button {...rest} disabled={disabled} style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            background: v.bg, color: v.fg, border: `1px solid ${v.bd}`,
            padding: '7px 12px', borderRadius: 7,
            fontSize: 12.5, fontWeight: 600, fontFamily: 'inherit',
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.55 : 1, whiteSpace: 'nowrap',
            ...style,
        }}>{leftIcon}{children}{rightIcon}</button>
    );
};

const Avatar = ({ name = '', size = 32 }) => {
    const initials = (name || '?').split(' ').map(s => s[0]).slice(0, 2).join('').toUpperCase();
    const palette = ['#2563eb', '#16a34a', '#d97706', '#dc2626', '#9333ea', '#0891b2', '#db2777'];
    const idx = (name?.charCodeAt(0) || 0) % palette.length;
    return (
        <div style={{
            width: size, height: size, borderRadius: 999,
            background: palette[idx], color: 'white',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: 600, fontSize: size * 0.4, flexShrink: 0,
        }}>{initials}</div>
    );
};

/* ── helpers ── */
const STUCK_DAYS = 2;

const stuckThresholdMs = STUCK_DAYS * 24 * 60 * 60 * 1000;

const daysSince = (iso) => {
    if (!iso) return 0;
    const ms = Date.now() - new Date(iso).getTime();
    return Math.max(0, Math.floor(ms / (24 * 60 * 60 * 1000)));
};

const statusFor = (pct, updatedAt, adminReviewed) => {
    if (adminReviewed) return 'approved';
    if (pct === 100) {
        const w = updatedAt ? Date.now() - new Date(updatedAt).getTime() : 0;
        return w > stuckThresholdMs ? 'overdue' : 'ready';
    }
    if (updatedAt && Date.now() - new Date(updatedAt).getTime() > stuckThresholdMs) return 'stuck';
    return 'progress';
};

const STATUS_LABEL = {
    ready:    { label: 'Ready for review', tone: 'amber' },
    progress: { label: 'In progress',      tone: 'blue' },
    stuck:    { label: 'Stuck',            tone: 'red' },
    overdue:  { label: 'Overdue · review', tone: 'red' },
    approved: { label: 'Approved',         tone: 'green' },
};

const monthStart = (() => { const d = new Date(); d.setDate(1); d.setHours(0,0,0,0); return d.getTime(); })();

const sectionOrder = [
    { key: 'basicInfo',          label: 'Personal info', icon: User },
    { key: 'employmentDetails',  label: 'Employment',    icon: Briefcase },
    { key: 'identityDocuments',  label: 'Identity docs', icon: FileText },
    { key: 'educationalDocuments', label: 'Education',   icon: FileText },
    { key: 'payrollBanking',     label: 'Banking',       icon: Landmark },
];

/* ── main ── */
const AdminOnboardingReview = ({ defaultFilter = 'all' }) => {
    const themeCtx = useTheme();
    const isDark = themeCtx?.isDarkMode ?? (themeCtx?.theme === 'dark');
    const p = useMemo(() => buildPalette(!!isDark), [isDark]);
    const {
        onboardingData,
        loading,
        fetchOnboardingData,
        reviewAndApprove,
        requestChanges,
        getCompletionPercentage,
        getItemStatus,
    } = useOnboarding();
    const { employees } = useEmployees();
    const { user } = useAuth();

    const [filter, setFilter] = useState(defaultFilter); // all | ready | progress | stuck | approved
    const [search, setSearch] = useState('');
    const [selectedId, setSelectedId] = useState(null);
    const [busyId, setBusyId] = useState(null);
    const [approveError, setApproveError] = useState(null);
    const [requestChangesFor, setRequestChangesFor] = useState(null); // id of row whose modal is open
    const [changesText, setChangesText] = useState('');
    const [requestBusy, setRequestBusy] = useState(false);
    const [requestError, setRequestError] = useState(null);
    // Toast: { kind: 'approved'|'requested', name: string } | null
    const [toast, setToast] = useState(null);
    useEffect(() => {
        if (!toast) return;
        const t = setTimeout(() => setToast(null), 4000);
        return () => clearTimeout(t);
    }, [toast]);

    /* enrich each employee row with the data the queue needs */
    const rows = useMemo(() => {
        return (employees || []).map((emp) => {
            const rec = onboardingData?.[emp.id] || {};
            const pct = Math.round(getCompletionPercentage(emp.id) || 0);
            const updatedAt = rec.updatedAt || rec.lastModified || null;
            const status = statusFor(pct, updatedAt, rec.adminReviewed);
            const dept = [emp.department, emp.designation].filter(Boolean).join(' · ');
            return {
                id: emp.id,
                employee: emp,
                record: rec,
                name: emp.display_name || `${emp.first_name || ''} ${emp.last_name || ''}`.trim() || emp.email,
                dept,
                pct,
                status,
                waitingDays: daysSince(updatedAt),
            };
        });
    }, [employees, onboardingData, getCompletionPercentage]);

    /* counts for the stat strip + filter chips */
    const counts = useMemo(() => {
        const c = { all: rows.length, ready: 0, progress: 0, stuck: 0, approved_mtd: 0 };
        rows.forEach((r) => {
            if (r.status === 'ready' || r.status === 'overdue') c.ready++;
            else if (r.status === 'stuck') c.stuck++;
            else if (r.status === 'progress') c.progress++;
            const updatedAt = r.record.updatedAt || r.record.lastModified;
            if (r.record.adminReviewed && updatedAt && new Date(updatedAt).getTime() >= monthStart) c.approved_mtd++;
        });
        return c;
    }, [rows]);

    /* apply filter + search */
    const visibleRows = useMemo(() => {
        const q = search.trim().toLowerCase();
        return rows.filter((r) => {
            if (filter === 'ready' && !(r.status === 'ready' || r.status === 'overdue')) return false;
            if (filter === 'progress' && r.status !== 'progress') return false;
            if (filter === 'stuck' && r.status !== 'stuck') return false;
            if (filter === 'approved' && r.status !== 'approved') return false;
            if (q && !(r.name.toLowerCase().includes(q) || r.dept.toLowerCase().includes(q))) return false;
            return true;
        });
    }, [rows, filter, search]);

    // default-select the first visible row whenever the filter changes
    useEffect(() => {
        if (!selectedId || !visibleRows.find(r => r.id === selectedId)) {
            setSelectedId(visibleRows[0]?.id || null);
        }
    }, [filter, visibleRows, selectedId]);

    const selected = visibleRows.find(r => r.id === selectedId) || rows.find(r => r.id === selectedId);

    const handleApprove = async (employeeId) => {
        setBusyId(employeeId);
        setApproveError(null);
        // Capture the name BEFORE the optimistic flip so the toast says who we approved.
        const row = rows.find((r) => r.id === employeeId);
        const name = row?.name || 'employee';
        try {
            const result = await reviewAndApprove(employeeId, user?.id, user?.display_name || user?.first_name);
            if (result && result.success === false) {
                setApproveError(`Approval failed: ${result.error?.response?.data?.message || result.error?.message || 'Unknown error'}`);
                return;
            }
            setToast({ kind: 'approved', name });
        } catch (e) {
            setApproveError(`Approval failed: ${e?.message || 'Unknown error'}`);
        } finally {
            setBusyId(null);
        }
    };

    const openRequestChanges = (employeeId) => {
        setRequestChangesFor(employeeId);
        setChangesText('');
        setRequestError(null);
    };

    const submitRequestChanges = async () => {
        if (!requestChangesFor) return;
        const reason = changesText.trim();
        if (!reason) { setRequestError('Please describe what needs to be changed.'); return; }
        setRequestBusy(true);
        setRequestError(null);
        const row = rows.find((r) => r.id === requestChangesFor);
        const name = row?.name || 'employee';
        try {
            const result = await requestChanges(requestChangesFor, reason, user?.id, user?.display_name || user?.first_name);
            if (result && result.success === false) {
                setRequestError(`Failed: ${result.error?.response?.data?.message || result.error?.message || 'Unknown error'}`);
                return;
            }
            setRequestChangesFor(null);
            setChangesText('');
            setToast({ kind: 'requested', name });
        } catch (e) {
            setRequestError(`Failed: ${e?.message || 'Unknown error'}`);
        } finally {
            setRequestBusy(false);
        }
    };

    const handleRefresh = () => fetchOnboardingData(true);

    return (
        <PaletteCtx.Provider value={p}>
        <div style={{ minHeight: 'calc(100vh - 80px)', padding: 28, background: p.appBg, fontFamily: 'Inter, system-ui, sans-serif', color: p.textPrimary }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 18 }}>
                <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, letterSpacing: -0.4, color: p.textPrimary }}>Onboarding</h1>
                <span style={{ fontSize: 13, color: p.textMuted }}>
                    · {counts.progress + counts.stuck} in progress · {counts.ready} waiting for review
                </span>
                <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                    <Btn variant="secondary" leftIcon={<RefreshCw size={13} />} onClick={handleRefresh} disabled={loading}>
                        {loading ? 'Refreshing…' : 'Refresh'}
                    </Btn>
                </div>
            </div>

            {/* Stat strip */}
            <div className="tour-onboarding-summary" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 18 }}>
                <StatCard label="Ready for review" value={counts.ready} tone="amber" icon={AlertTriangle} />
                <StatCard label="In progress"      value={counts.progress} tone="blue"  icon={Clock} />
                <StatCard label="Stuck (>2 days)"  value={counts.stuck} tone="red"   icon={AlertTriangle} />
                <StatCard label="Approved · MTD"   value={counts.approved_mtd} tone="green" icon={CheckCircle2} />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 420px', gap: 16, alignItems: 'start' }}>
                {/* TABLE */}
                <Card padding={0} style={{ overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                    {/* Toolbar */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '14px 18px', borderBottom: `1px solid ${p.borderSoft}` }}>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            <FilterChip label="All"           count={counts.all}     active={filter === 'all'}      onClick={() => setFilter('all')} />
                            <FilterChip label="Ready"         count={counts.ready}   active={filter === 'ready'}    onClick={() => setFilter('ready')} />
                            <FilterChip label="In progress"   count={counts.progress} active={filter === 'progress'} onClick={() => setFilter('progress')} />
                            <FilterChip label="Stuck"         count={counts.stuck}   tone="red" active={filter === 'stuck'} onClick={() => setFilter('stuck')} />
                            <FilterChip label="Approved"      count={counts.approved_mtd} active={filter === 'approved'} onClick={() => setFilter('approved')} />
                        </div>
                        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
                            <input
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search by name or dept"
                                style={{
                                    background: p.inputBg, border: `1px solid ${p.inputBorder}`, borderRadius: 8,
                                    padding: '6px 10px', fontSize: 12.5, color: p.inputText,
                                    fontFamily: 'inherit', minWidth: 220,
                                }}
                            />
                            <Btn variant="secondary" leftIcon={<Filter size={13} />} disabled>Department</Btn>
                            <Btn variant="dark" leftIcon={<Plus size={13} />} disabled>Invite</Btn>
                        </div>
                    </div>

                    <div style={{ maxHeight: 'calc(100vh - 320px)', overflow: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, color: p.textPrimary }}>
                            <thead>
                                <tr style={{ background: p.surface2, color: p.textMuted, fontSize: 11, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase' }}>
                                    <th style={{ ...th, paddingLeft: 18 }}>Employee</th>
                                    <th style={th}>Progress</th>
                                    <th style={th}>Status</th>
                                    <th style={th}>Waiting</th>
                                    <th style={{ ...th, textAlign: 'right', paddingRight: 18 }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {visibleRows.length === 0 ? (
                                    <tr>
                                        <td colSpan={5} style={{ padding: '60px 18px', textAlign: 'center', color: p.textMuted, fontSize: 13 }}>
                                            {loading ? 'Loading…' : 'No employees match this filter.'}
                                        </td>
                                    </tr>
                                ) : visibleRows.map((r) => (
                                    <tr
                                        key={r.id}
                                        onClick={() => setSelectedId(r.id)}
                                        style={{
                                            borderTop: `1px solid ${p.borderSoft}`,
                                            background: r.id === selectedId ? p.selectedRow : 'transparent',
                                            cursor: 'pointer',
                                        }}
                                    >
                                        <td style={{ ...td, paddingLeft: 18 }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                <Avatar name={r.name} size={32} />
                                                <div>
                                                    <div style={{ fontWeight: 700, color: p.textPrimary }}>{r.name}</div>
                                                    <div style={{ fontSize: 12, color: p.textMuted, marginTop: 1 }}>{r.dept || '—'}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td style={td}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 140 }}>
                                                <div style={{ flex: 1, height: 6, background: p.progressTrack, borderRadius: 999, overflow: 'hidden' }}>
                                                    <div style={{
                                                        width: `${r.pct}%`, height: '100%',
                                                        background: r.pct === 100 ? '#16a34a' : r.pct >= 70 ? '#2563eb' : r.pct >= 40 ? '#f59e0b' : '#ef4444',
                                                        borderRadius: 999, transition: 'width .3s',
                                                    }} />
                                                </div>
                                                <span style={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', minWidth: 38, textAlign: 'right', color: p.textPrimary }}>{r.pct}%</span>
                                            </div>
                                        </td>
                                        <td style={td}>
                                            <Pill tone={STATUS_LABEL[r.status].tone}>{STATUS_LABEL[r.status].label}</Pill>
                                        </td>
                                        <td style={{ ...td, color: r.waitingDays >= STUCK_DAYS + 1 ? '#fca5a5' : p.textSecondary, fontWeight: r.waitingDays >= STUCK_DAYS + 1 ? 700 : 500 }}>
                                            {r.waitingDays === 0 ? 'Today' : `${r.waitingDays} day${r.waitingDays > 1 ? 's' : ''}`}
                                        </td>
                                        <td style={{ ...td, textAlign: 'right', paddingRight: 18 }} onClick={(e) => e.stopPropagation()}>
                                            {(r.status === 'ready' || r.status === 'overdue') && (
                                                <Btn variant={r.status === 'overdue' ? 'success' : 'primary'} onClick={() => handleApprove(r.id)} disabled={busyId === r.id}>
                                                    {busyId === r.id ? '…' : r.status === 'overdue' ? 'Approve' : 'Review'}
                                                </Btn>
                                            )}
                                            {(r.status === 'progress' || r.status === 'stuck') && (
                                                <Btn variant="secondary" disabled title="Nudge endpoint not yet implemented">Nudge</Btn>
                                            )}
                                            {r.status === 'approved' && <Pill tone="green">Approved</Pill>}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Card>

                {/* DRAWER */}
                {selected ? (
                    <Card padding={0} style={{ overflow: 'hidden', display: 'flex', flexDirection: 'column', position: 'sticky', top: 24, maxHeight: 'calc(100vh - 80px)' }}>
                        <DrawerHeader selected={selected} onClose={() => setSelectedId(null)} />
                        <div style={{ flex: 1, overflow: 'auto', padding: 18, display: 'flex', flexDirection: 'column', gap: 14 }}>
                            <DrawerProgress selected={selected} />
                            <DrawerSections selected={selected} getItemStatus={getItemStatus} />
                        </div>
                        {approveError && (
                            <div style={{ padding: '8px 14px', background: p.pillRedBg, color: p.pillRedFg, fontSize: 12, borderTop: `1px solid ${p.borderSoft}` }}>
                                {approveError}
                            </div>
                        )}
                        <div style={{ padding: 14, borderTop: `1px solid ${p.borderSoft}`, display: 'flex', gap: 8, background: p.surface2 }}>
                            <Btn
                                variant="danger"
                                style={{ flex: 1, justifyContent: 'center' }}
                                onClick={() => openRequestChanges(selected.id)}
                                disabled={selected.status === 'approved'}
                            >
                                Request changes
                            </Btn>
                            <Btn
                                variant="success"
                                style={{ flex: 1, justifyContent: 'center' }}
                                leftIcon={<Check size={14} strokeWidth={2.5} />}
                                onClick={() => handleApprove(selected.id)}
                                disabled={busyId === selected.id || selected.status === 'approved' || selected.pct < 100}
                            >
                                {selected.status === 'approved' ? 'Approved' : busyId === selected.id ? 'Approving…' : 'Approve'}
                            </Btn>
                        </div>
                    </Card>
                ) : (
                    <Card style={{ padding: 24, color: p.textMuted, fontSize: 13 }}>
                        Select an employee from the queue to review their submission.
                    </Card>
                )}
            </div>

            {requestChangesFor && (
                <div
                    onClick={() => !requestBusy && setRequestChangesFor(null)}
                    style={{
                        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        zIndex: 1000, padding: 24,
                    }}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            background: p.surface, color: p.textPrimary,
                            border: `1px solid ${p.border}`, borderRadius: 12,
                            width: '100%', maxWidth: 480,
                            boxShadow: '0 24px 60px -12px rgba(0,0,0,0.45)',
                        }}
                    >
                        <div style={{ padding: '16px 18px', borderBottom: `1px solid ${p.borderSoft}` }}>
                            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: p.textPrimary }}>Request changes</h3>
                            <p style={{ margin: '4px 0 0', fontSize: 12.5, color: p.textMuted }}>
                                The employee will be notified and can edit their submission. Tell them what needs to change.
                            </p>
                        </div>
                        <div style={{ padding: 18 }}>
                            <label style={{ fontSize: 12.5, fontWeight: 600, color: p.textSecondary, display: 'block', marginBottom: 6 }}>
                                Reason
                            </label>
                            <textarea
                                value={changesText}
                                onChange={(e) => setChangesText(e.target.value)}
                                placeholder="e.g. Aadhaar copy is blurred — please re-upload a clearer scan."
                                rows={5}
                                autoFocus
                                style={{
                                    width: '100%',
                                    background: p.inputBg, color: p.inputText,
                                    border: `1px solid ${p.inputBorder}`, borderRadius: 8,
                                    padding: '10px 12px', fontSize: 14, fontFamily: 'inherit',
                                    resize: 'vertical',
                                }}
                            />
                            {requestError && (
                                <div style={{ marginTop: 8, fontSize: 12, color: '#fca5a5' }}>{requestError}</div>
                            )}
                        </div>
                        <div style={{ padding: '12px 18px', borderTop: `1px solid ${p.borderSoft}`, display: 'flex', justifyContent: 'flex-end', gap: 8, background: p.surface2 }}>
                            <Btn variant="ghost" onClick={() => setRequestChangesFor(null)} disabled={requestBusy}>Cancel</Btn>
                            <Btn variant="danger" onClick={submitRequestChanges} disabled={requestBusy}>
                                {requestBusy ? 'Sending…' : 'Send request'}
                            </Btn>
                        </div>
                    </div>
                </div>
            )}

            {/* Success toast — bottom-right, auto-dismisses after 4s */}
            {toast && (
                <div
                    role="status"
                    aria-live="polite"
                    style={{
                        position: 'fixed', right: 28, bottom: 28, zIndex: 1100,
                        display: 'flex', alignItems: 'center', gap: 12,
                        padding: '12px 16px',
                        background: toast.kind === 'approved' ? '#16a34a' : '#f59e0b',
                        color: 'white',
                        borderRadius: 12,
                        boxShadow: '0 18px 40px -10px rgba(0,0,0,0.45)',
                        minWidth: 280, maxWidth: 420,
                        animation: 'oakhrToastIn 220ms cubic-bezier(0.22,1,0.36,1)',
                    }}
                >
                    <span style={{
                        width: 28, height: 28, borderRadius: 999,
                        background: 'rgba(255,255,255,0.2)',
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>
                        <Check size={16} strokeWidth={3} />
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13.5, fontWeight: 700, lineHeight: 1.15 }}>
                            {toast.kind === 'approved' ? 'Onboarding approved' : 'Changes requested'}
                        </div>
                        <div style={{ fontSize: 12, opacity: 0.92, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {toast.kind === 'approved'
                                ? `${toast.name} has been signed off.`
                                : `${toast.name} was notified to update their submission.`}
                        </div>
                    </div>
                    <button
                        onClick={() => setToast(null)}
                        aria-label="Dismiss"
                        style={{
                            background: 'transparent', border: 'none', color: 'rgba(255,255,255,0.85)',
                            cursor: 'pointer', padding: 4, display: 'inline-flex',
                        }}
                    >
                        <X size={14} />
                    </button>
                </div>
            )}
            <style>{`@keyframes oakhrToastIn { from { transform: translateY(12px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }`}</style>
        </div>
        </PaletteCtx.Provider>
    );
};

/* ── pieces ── */

const StatCard = ({ label, value, tone, icon: Icon }) => {
    const p = useP();
    const palette = {
        amber: { fg: p.pillAmberFg, bg: p.pillAmberBg },
        blue:  { fg: p.pillBlueFg,  bg: p.pillBlueBg },
        red:   { fg: p.pillRedFg,   bg: p.pillRedBg },
        green: { fg: p.pillGreenFg, bg: p.pillGreenBg },
    }[tone];
    return (
        <Card padding={16}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: palette.fg }}>
                <span style={{ width: 28, height: 28, borderRadius: 7, background: palette.bg, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Icon size={16} />
                </span>
                <span style={{ fontSize: 12, fontWeight: 600, color: p.textMuted, letterSpacing: 0.3, textTransform: 'uppercase' }}>{label}</span>
            </div>
            <div style={{ marginTop: 10, fontSize: 30, fontWeight: 800, letterSpacing: -0.6, fontVariantNumeric: 'tabular-nums', color: p.textPrimary }}>{value}</div>
        </Card>
    );
};

const FilterChip = ({ label, count, active, tone, onClick }) => {
    const p = useP();
    const palette = tone === 'red'
        ? { bg: active ? p.pillRedBg : 'transparent', fg: p.pillRedFg, bd: active ? p.pillRedBd : p.border }
        : { bg: active ? p.chipActiveBg : p.chipBg, fg: active ? p.chipActiveFg : p.chipFg, bd: active ? p.chipActiveBd : p.chipBd };
    return (
        <button onClick={onClick} style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '6px 11px', borderRadius: 999,
            background: palette.bg, color: palette.fg, border: `1px solid ${palette.bd}`,
            fontSize: 12.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
        }}>
            {label}<span style={{ fontSize: 11, opacity: 0.7 }}>{count}</span>
        </button>
    );
};

const DrawerHeader = ({ selected, onClose }) => {
    const p = useP();
    return (
        <div style={{ padding: '16px 18px', borderBottom: `1px solid ${p.borderSoft}`, display: 'flex', alignItems: 'center', gap: 12 }}>
            <Avatar name={selected.name} size={44} />
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: p.textPrimary }}>{selected.name}</div>
                <div style={{ fontSize: 12, color: p.textMuted }}>
                    {selected.dept || '—'}
                    {selected.employee?.joining_date && ` · Joins ${new Date(selected.employee.joining_date).toLocaleDateString()}`}
                </div>
            </div>
            <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: p.textMuted, cursor: 'pointer', padding: 4 }}>
                <X size={16} />
            </button>
        </div>
    );
};

const DrawerProgress = ({ selected }) => {
    const p = useP();
    const updatedAt = selected.record.updatedAt || selected.record.lastModified;
    const submittedLabel = updatedAt
        ? `Last activity ${daysSince(updatedAt) === 0 ? 'today' : `${daysSince(updatedAt)} day${daysSince(updatedAt) > 1 ? 's' : ''} ago`}`
        : 'No activity yet';
    const tone = selected.pct === 100 ? { fg: p.pillGreenFg, bg: p.pillGreenBg, bd: p.pillGreenBd, bar: '#16a34a', track: 'rgba(22,163,74,0.2)' }
        : selected.pct >= 70 ? { fg: p.pillBlueFg, bg: p.pillBlueBg, bd: p.pillBlueBd, bar: '#2563eb', track: 'rgba(37,99,235,0.2)' }
        : { fg: p.pillAmberFg, bg: p.pillAmberBg, bd: p.pillAmberBd, bar: '#f59e0b', track: 'rgba(245,158,11,0.2)' };
    return (
        <div style={{ padding: 14, background: tone.bg, border: `1px solid ${tone.bd}`, borderRadius: 10 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: tone.fg }}>{submittedLabel}</span>
                <span style={{ fontSize: 18, fontWeight: 800, color: tone.fg }}>{selected.pct}%</span>
            </div>
            <div style={{ height: 5, background: tone.track, borderRadius: 999 }}>
                <div style={{ width: `${selected.pct}%`, height: '100%', background: tone.bar, borderRadius: 999, transition: 'width .3s' }} />
            </div>
        </div>
    );
};

const DrawerSections = ({ selected, getItemStatus }) => {
    const p = useP();
    const fields = selected.record.fields || {};
    const documents = selected.record.documents || {};
    return (
        <>
            {sectionOrder.map(({ key, label, icon: Icon }) => {
                const section = ONBOARDING_CHECKLIST[key];
                if (!section) return null;
                const items = section.items.filter((it) => {
                    const st = getItemStatus ? getItemStatus(it.id, it.required) : (it.required ? 'required' : 'optional');
                    return st !== 'redundant' && st !== 'hidden';
                });
                if (items.length === 0) return null;
                return (
                    <div key={key}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                            <span style={{ width: 22, height: 22, borderRadius: 5, background: p.pillBlueBg, color: '#60a5fa', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Icon size={12} />
                            </span>
                            <span style={{ fontSize: 11, fontWeight: 700, color: p.textMuted, letterSpacing: 0.5, textTransform: 'uppercase' }}>{label}</span>
                        </div>
                        <div>
                            {items.map((it) => {
                                if (it.type === 'file') {
                                    const f = documents[it.id];
                                    return (
                                        <div key={it.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', fontSize: 12.5 }}>
                                            <div style={{ width: 24, height: 24, borderRadius: 5, background: f?.url ? p.pillGreenBg : p.surfaceMute, color: f?.url ? p.pillGreenFg : p.textMuted, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                                                <FileText size={12} />
                                            </div>
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div style={{ fontWeight: 600, color: p.textPrimary }}>{it.label}</div>
                                                {f?.fileName ? (
                                                    <div style={{ fontSize: 11, color: p.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.fileName}{f.fileSize ? ` · ${Math.round(f.fileSize / 1024)} KB` : ''}</div>
                                                ) : (
                                                    <div style={{ fontSize: 11, color: p.pillAmberFg }}>Not uploaded</div>
                                                )}
                                            </div>
                                            {f?.url ? (
                                                <a href={f.url} target="_blank" rel="noreferrer" style={{ color: p.textMuted, display: 'inline-flex' }}>
                                                    <Eye size={14} />
                                                </a>
                                            ) : null}
                                        </div>
                                    );
                                }
                                const v = fields[it.id];
                                const display = it.type === 'checkbox' ? (v ? 'Acknowledged' : '—') : (v ?? '—');
                                return (
                                    <div key={it.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', fontSize: 12.5 }}>
                                        <span style={{ color: p.textMuted }}>{it.label}</span>
                                        <span style={{ color: v ? p.textPrimary : p.textMuted, fontWeight: 500, textAlign: 'right', maxWidth: '60%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {display === '' ? '—' : display}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                );
            })}
        </>
    );
};

const th = { textAlign: 'left', padding: '10px 12px' };
const td = { padding: '12px 12px', verticalAlign: 'middle' };

export default AdminOnboardingReview;
